import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier.tsx';
import { MantineTextInput } from '@dealstack/frontend/shared/components/mantineForm/MantineTextInput';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Flex, Text } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { GenericDrawer } from '@dealstack/frontend/shared/components/GenericDrawer/GenericDrawer';
import {
	DELETE_DEAL_FORM,
	useDeleteDealController,
} from '@dealstack/frontend/app/pages/Project/DealSettings/DealGeneralSettings/useDeleteDealController';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';

interface Props {
	opened: boolean;
	onClose: () => void;
}

export const DeleteDealDrawer = ({ opened, onClose }: Props) => {
	const navigate = useNavigate();

	const handleCloseOnSuccess = () => {
		onClose();
		navigate(ROUTES.DASHBOARD);
	};

	const { deleteDealForm, dealData, deleteDealRequest, onSubmit } = useDeleteDealController({
		onClose: handleCloseOnSuccess,
	});
	const tAdminDashboard = useTranslationAdminDashboard();

	return (
		<GenericDrawer
			opened={opened}
			onClose={onClose}
			title={<Text size="B4Bold">{tAdminDashboard('v3.deal_card.delete_deal')}</Text>}
			body={
				<Flex direction="column" h="100%">
					{deleteDealRequest.isPending ? <InlineLoader /> : null}

					<FormProvider {...deleteDealForm}>
						<form
							onSubmit={deleteDealForm.handleSubmit(onSubmit)}
							style={{ height: '100%' }}
						>
							<Flex direction="column" justify="space-between" h="100%">
								<Flex direction="column" gap="1rem">
									<Flex direction="column" gap="0.25rem">
										<Text size="B6Bold">
											{tAdminDashboard('v3.delete_deal.deal_name')}
										</Text>
										<Text size="B5Light">{dealData?.name}</Text>
									</Flex>

									<Flex direction="column" gap="0.25rem">
										<Text size="sm" fw={600}>
											{tAdminDashboard('v3.delete_deal.target_name')}
										</Text>
										<Text size="B5Light">{dealData?.targetName}</Text>
									</Flex>

									<MantineTextInput
										data-testid={
											TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_PROJECT_MODAL_DELETE_DEAL_INPUT
										}
										name={DELETE_DEAL_FORM.CONFIRM_DEAL_NAME}
										labelProps={{ fw: 600, fz: 'md' }}
										descriptionProps={{
											fz: 'md',
											mt: '0.25rem',
											lh: '1.25rem',
										}}
										label={tAdminDashboard('v3.delete_deal.deal_name')}
										placeholder={tAdminDashboard('v3.delete_deal.placeholder')}
										description={tAdminDashboard(
											'v3.delete_deal.target_field.description'
										)}
										inputWrapperOrder={[
											'label',
											'error',
											'input',
											'description',
										]}
									/>
								</Flex>

								<Button
									data-testid={
										TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_PROJECT_MODAL_DELETE_DEAL_CONFIRM_BUTTON
									}
									type="submit"
									color="red"
									w="auto"
									disabled={
										!deleteDealForm.formState.isValid ||
										deleteDealRequest.isPending
									}
								>
									{tAdminDashboard('v3.edit_deal.delete')}
								</Button>
							</Flex>
						</form>
					</FormProvider>
				</Flex>
			}
		/>
	);
};
